import { Switch, Route, Redirect, Router as WouterRouter } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";

import { AuthProvider } from "@/context/auth-context";
import { Layout } from "@/components/layout";

import Login from "@/pages/login";
import Dashboard from "@/pages/dashboard";
import Today from "@/pages/today";
import Leads from "@/pages/leads";
import NewLead from "@/pages/lead-new";
import LeadDetail from "@/pages/lead-detail";
import Spaces from "@/pages/spaces";
import SpaceHubPage from "@/pages/space-hub";
import Team from "@/pages/team";
import ScoreBuilder from "@/pages/score-builder";
import ApiKeys from "@/pages/api-keys";
import ApiDocs from "@/pages/api-docs";
import LoginActivity from "@/pages/login-activity";
import Inventory from "@/pages/inventory";
import InventoryOperation from "@/pages/inventory-operation";
import InventorySpace from "@/pages/inventory-space";
import InventoryInfra from "@/pages/inventory-infra";
import SelfServiceConfirm from "@/pages/self-service-confirm";
import Billing from "@/pages/billing";
import BillingContracts from "@/pages/billing-contracts";
import BillingPlans from "@/pages/billing-plans";
import PlanEditorPage from "@/pages/plan-editor";
import BillingPackages from "@/pages/billing-packages";
import BillingDiscounts from "@/pages/billing-discounts";
import Expenses from "@/pages/expenses";
import Ledger from "@/pages/ledger";
import Members from "@/pages/members";
import MemberDetail from "@/pages/member-detail";
import MemberDirectory from "@/pages/member-directory";
import MemberProfile from "@/pages/member-profile";
import ManageMembershipPage from "@/pages/manage-membership";
import AccountsPage from "@/pages/accounts";
import AccountDetailPage from "@/pages/account-detail";
import ConvertLeadPage from "@/pages/convert-lead";
import AddPlanPage from "@/pages/add-plan";
import ViewPlanPage from "@/pages/view-plan";
import SpaceDesignerPage from "@/features/space-designer/SpaceDesignerPage";
import SvgLibraryPage from "@/features/space-designer/SvgLibraryPage";
import NewBooking from "@/pages/new-booking";
import AttendancePage from "@/pages/attendance";
import ResourcePricing from "@/pages/resource-pricing";
import ReceiptInvoiceSettings from "@/pages/receipt-invoice-settings";
import TaskCreatePage from "@/pages/tasks/task-create";
import TaskDetailPage from "@/pages/tasks/task-detail";
import TaskSearchPage from "@/pages/tasks/task-search";
import TaskCategorySettingsPage from "@/pages/tasks/settings/categories-settings";
import TaskSlaSettingsPage from "@/pages/tasks/settings/sla-settings";
import TaskRoutingSettingsPage from "@/pages/tasks/settings/routing-settings";
import TaskEscalationSettingsPage from "@/pages/tasks/settings/escalation-settings";
import TaskChecklistSettingsPage from "@/pages/tasks/settings/checklists-settings";
import ChecklistPage from "@/pages/tasks/checklist";

const queryClient = new QueryClient();

function Router() {
  return (
    <Switch>
      {/* Token-gated standalone — no Layout, no AuthProvider dependency */}
      <Route path="/m/confirm/:token" component={SelfServiceConfirm} />
      {/* Everything else flows through Layout */}
      <Route>
        <Layout>
          <Switch>
            <Route path="/login" component={Login} />
            <Route path="/" component={Dashboard} />
            <Route path="/today" component={Today} />
            {/* Phase 41: Tasks module. New/Search are literal paths and must
                precede the /tasks/:id param route (wouter first-match-wins —
                same convention as /accounts/convert/:leadId before /accounts/:id). */}
            <Route path="/tasks/new" component={TaskCreatePage} />
            <Route path="/tasks/search" component={TaskSearchPage} />
            {/* Phase 41 Plan 10: config Settings screens (TCFG-01..04), admin+ops_lead
                gated client-side (mirrors server's canEditConfig) — literal paths,
                registered before /tasks/:id per the same first-match-wins convention. */}
            <Route path="/tasks/settings/categories" component={TaskCategorySettingsPage} />
            <Route path="/tasks/settings/sla" component={TaskSlaSettingsPage} />
            <Route path="/tasks/settings/routing" component={TaskRoutingSettingsPage} />
            <Route path="/tasks/settings/escalation" component={TaskEscalationSettingsPage} />
            {/* Phase 43 Plan 06 (43-RESEARCH Pattern 11): the read-only "Daily
                checklist" tab + its Settings editor — literal paths, same
                first-match-wins convention, registered before /tasks/:id. */}
            <Route path="/tasks/settings/checklists" component={TaskChecklistSettingsPage} />
            {/* Phase 49 Plan 08: restructured into a tabbed Checklist page
                (Daily/Weekly/Monthly/Onboarding/Exit) — /tasks/checklist/:tab
                registered BEFORE the bare /tasks/checklist (default 'daily')
                and BEFORE /tasks/:id, mirroring inventory-operation.tsx's
                literal-before-param convention. */}
            <Route path="/tasks/checklist/:tab" component={ChecklistPage} />
            <Route path="/tasks/checklist" component={ChecklistPage} />
            <Route path="/tasks/:id" component={TaskDetailPage} />
            <Route path="/tasks"><Redirect to="/tasks/search" /></Route>
            <Route path="/leads" component={Leads} />
            <Route path="/leads/new" component={NewLead} />
            <Route path="/leads/:id/edit" component={NewLead} />
            <Route path="/leads/:id" component={LeadDetail} />
            {/* Phase 54 Plan 06 (SPACE-UX-HUB): center-detail hub. Registered
                BEFORE the bare /spaces so wouter's first-match-wins Switch
                resolves the param route, mirroring /members/:id before
                /members and /accounts/:id before /accounts. */}
            <Route path="/spaces/:id" component={SpaceHubPage} />
            <Route path="/spaces" component={Spaces} />
            <Route path="/team" component={Team} />
            <Route path="/score-builder" component={ScoreBuilder} />
            <Route path="/api-keys" component={ApiKeys} />
            <Route path="/api-docs" component={ApiDocs} />
            <Route path="/login-activity" component={LoginActivity} />
            <Route path="/inventory/operation/categories" component={InventoryOperation} />
            <Route path="/inventory/operation/vendors"    component={InventoryOperation} />
            <Route path="/inventory/operation/items"      component={InventoryOperation} />
            <Route path="/inventory/operation/stock-in"   component={InventoryOperation} />
            <Route path="/inventory/operation/stock-out"  component={InventoryOperation} />
            <Route path="/inventory/operation/day-close"  component={InventoryOperation} />
            <Route path="/inventory/operation/locations"  component={InventoryOperation} />
            <Route path="/inventory/operation/alerts"     component={InventoryOperation} />
            <Route path="/inventory/operation"            component={InventoryOperation} />
            {/* Phase 36: Space Designer (ported SDS engine). Specific sub-routes
                first — "svg-library" must precede ":floorId" so wouter doesn't
                capture it as a floor id. */}
            <Route path="/inventory/space/designer/svg-library" component={SvgLibraryPage} />
            <Route path="/inventory/space/designer/:floorId" component={SpaceDesignerPage} />
            <Route path="/inventory/space/designer" component={SpaceDesignerPage} />
            {/* Phase 54 (SPACE-UX-RETIRE): the duplicate Konva floor-planner tab
                is retired — Space Designer is the sole floor-plan editor. */}
            <Route path="/inventory/space/floor-plan"><Redirect to="/inventory/space/designer" /></Route>
            <Route path="/inventory/space/resources"  component={InventorySpace} />
            <Route path="/inventory/space/occupancy"  component={InventorySpace} />
            <Route path="/inventory/space"            component={InventorySpace} />
            <Route path="/inventory/infra/catalog"      component={InventoryInfra} />
            <Route path="/inventory/infra/acquisitions" component={InventoryInfra} />
            <Route path="/inventory/infra/warranty"     component={InventoryInfra} />
            <Route path="/inventory/infra/categories"   component={InventoryInfra} />
            <Route path="/inventory/infra"              component={InventoryInfra} />
            <Route path="/inventory/hsn-codes"   component={Inventory} />
            <Route path="/inventory/charge-types" component={Inventory} />
            <Route path="/inventory"           component={Inventory} />
            <Route path="/expenses"            component={Expenses} />
            <Route path="/ledger"              component={Ledger} />
            <Route path="/attendance"          component={AttendancePage} />
            {/* Phase 36: the standalone Konva floor-planner was retired in favour
                of the Space Designer (/inventory/space/designer). */}
            {/* Phase 23-07: Accounts routes (new top-level module) */}
            {/* 2026-06-10: Convert Lead is a page route — must precede the
                catch-all /accounts/:id detail route so wouter matches it first. */}
            <Route path="/accounts/convert/:leadId" component={ConvertLeadPage} />
            <Route path="/accounts/:id/add-plan" component={AddPlanPage} />
            <Route path="/accounts/:id/plans/:membershipId" component={ViewPlanPage} />
            <Route path="/accounts/:id" component={AccountDetailPage} />
            <Route path="/accounts" component={AccountsPage} />
            {/* Phase 38 (Screen 3): Manage-membership editor — registered BEFORE
                the bare /members/:id detail route so wouter's first-match-wins
                Switch doesn't need to disambiguate (2-segment vs 1-segment
                paths never collide, but keeping the more specific route first
                matches house convention, e.g. /accounts/:id/plans/:membershipId). */}
            <Route path="/members/:id/manage" component={ManageMembershipPage} />
            {/* Phase 37: centralised, contact-keyed Member directory + detail */}
            <Route path="/members/:id" component={MemberProfile} />
            <Route path="/members" component={MemberDirectory} />
            {/* Legacy member-detail kept for direct navigation during transition */}
            <Route path="/members-legacy/:id" component={MemberDetail} />
            <Route path="/members-legacy" component={Members} />
            <Route path="/booking/new" component={NewBooking} />
            <Route path="/settings/resource-pricing" component={ResourcePricing} />
            <Route path="/settings/documents" component={ReceiptInvoiceSettings} />
            {/* Consolidated: old per-doc settings now live on one page */}
            <Route path="/settings/invoice-template"><Redirect to="/settings/documents" /></Route>
            <Route path="/settings/receipt"><Redirect to="/settings/documents" /></Route>
            <Route path="/settings"><Redirect to="/settings/documents" /></Route>
            <Route path="/billing/today"                component={Billing} />
            <Route path="/billing/invoices"             component={Billing} />
            <Route path="/billing/charges"              component={Billing} />
            <Route path="/billing/ledger/:memberId"     component={Billing} />
            <Route path="/billing/contracts"            component={BillingContracts} />
            <Route path="/billing/plans/new"            component={PlanEditorPage} />
            <Route path="/billing/plans/:id"            component={PlanEditorPage} />
            <Route path="/billing/plans"                component={BillingPlans} />
            <Route path="/billing/packages"             component={BillingPackages} />
            <Route path="/billing/discounts"            component={BillingDiscounts} />
            <Route path="/billing"                      component={Billing} />
            <Route path="/operations/stock-out">{() => <Redirect to="/inventory/operation/stock-out" />}</Route>
            <Route path="/operations">{() => <Redirect to="/inventory/operation/stock-in" />}</Route>
            <Route component={NotFound} />
          </Switch>
        </Layout>
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <AuthProvider>
            <Router />
          </AuthProvider>
        </WouterRouter>
        <Toaster />
        {/* Phase 41 deviation (Rule 3 — blocking issue): the sonner package is a
            dependency and 20+ existing CRM files already call toast() from "sonner",
            but sonner's own <Toaster/> was never mounted anywhere, so none of those
            calls ever rendered. Mounted here (plain sonner import, not the
            components/ui/sonner.tsx next-themes wrapper, to avoid pulling in an
            unconfigured theme provider) so task-detail.tsx's TASK-11 conflict toast
            (and every other pre-existing sonner call in the app) is now visible. */}
        <SonnerToaster richColors closeButton position="top-right" />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
